const API_URL = "/wordpress/wp-json/wp/v2";

// Helper function for deep fallback
function _deepFallback ( target, fallback ) {
    if ( !target || !fallback ) return target || fallback;

    // If target is not an object, return it or fallback
    if ( typeof target !== "object" || target === null ) {
        return _isEmpty( target ) ? fallback : target;
    }

    // If target is an array
    if ( Array.isArray( target ) ) {
        return target.length === 0 ? fallback : target;
    }

    // For objects, merge properties
    const result = { ...target };

    Object.keys( fallback ).forEach( ( key ) => {
        if ( _isEmpty( result[ key ] ) ) {
            result[ key ] = fallback[ key ];
        } else if (
            typeof result[ key ] === "object" &&
            typeof fallback[ key ] === "object"
        ) {
            result[ key ] = _deepFallback( result[ key ], fallback[ key ] );
        }
    } );

    return result;
}
// Helper function to check if value is empty
function _isEmpty ( value ) {
    if ( value === null || value === undefined || value === "" ) {
        return true;
    }
    if ( Array.isArray( value ) ) {
        return value.length === 0;
    }
    if ( typeof value === "object" ) {
        return Object.keys( value ).length === 0;
    }
    return false;
}

// Term dictionary for caching taxonomy term names across calls
// Key format: `${ taxonomy }:${ id }` -> name
const _termDictionary = new Map();

function _getTermDictKey ( taxonomy, id ) {
    return `${ taxonomy }:${ id }`;
}

function _chunkArray ( array, size = 100 ) {
    const result = [];
    for ( let i = 0; i < array.length; i += size ) {
        result.push( array.slice( i, i + size ) );
    }
    return result;
}

export const useFetchAPI = () => {

    const { locale } = useI18n();
    const isPending = ref( true );
    const nuxtApp = useNuxtApp();

    const fetchPostCategory = async ( type = "news" ) => {

        isPending.value = true;

        try {
            const cacheKey = `fetchPostCategory-${ type }-${ locale.value }`;
            const { data, error } = await useFetch(
                () => `${ API_URL }/${ type }-category?lang=${ locale.value }`,
                {
                    key: cacheKey, // Use locale-specific cache key
                    transform: ( data ) =>
                        data.map( ( item ) => ( {
                            id: item.id,
                            name: item.name,
                            slug: item.slug,
                            language: item.language,
                        } ) ),
                },
            );

            return { data, error };
        } catch ( err ) {
            return { data: ref( [] ), error: ref( err ) };
        } finally {
            isPending.value = false;
        }
    };
    const fetchPostTaxonomy = async ( type = "news" ) => {

        isPending.value = true;

        try {
            const { data, error } = await useFetch(
                () => `${ API_URL }/${ type }-taxonomy?per_page=100`,
                {
                    key: `fetchPostTaxonomy-${ type }`,
                    transform: ( data ) =>
                        data.map( ( item ) => ( {
                            id: item.id,
                            name: item.name,
                            slug: item.slug,
                            language: item.language,
                        } ) ),
                },
            );

            return { data, error };
        } catch ( err ) {
            // console.error("Error in fetchPostTaxonomy:", err);
            return { data: ref( [] ), error: ref( err ) };
        } finally {
            isPending.value = false;
        }
    };
    const fetchPage = async ( slug ) => {

        isPending.value = true;

        try {
            // Fetch all language pages in one API call
            const { data, error } = await useFetch(
                () => `${ API_URL }/pages?slug=${ slug }`,
                {
                    key: `fetchPage-${ slug }`,
                    transform: ( data ) => {
                        const languageData = {};
                        data.forEach( ( page ) => {
                            if ( page.language ) {
                                languageData[ page.language ] = {
                                    featured_media: page.featured_media,
                                    meta_box: page.meta_box,
                                    seo: page.seo,
                                    excerpt: page.excerpt,
                                };
                            }
                        } );

                        return languageData;
                    },
                },
            );

            // Create a reactive computed that returns current language data
            const currentData = computed( () => data.value[ locale.value ] || data.value.zh || null );

            return {
                data: currentData,
                error: error,
                allLanguageData: computed( () => data.value ),
            };
        } catch ( err ) {
            // console.error( "Error in fetchPage:", err );
            return {
                data: ref( null ),
                error: ref( err ),
                allLanguageData: ref( {} ),
            };
        } finally {

            isPending.value = false;

        }

    };
    const fetchHomePage = async () => {

        isPending.value = true;

        try {
            const { data, error } = await useFetch(
                () => `${ API_URL }/pages?slug=home`,
                {
                    key: `fetchPage-home`,
                    transform: async ( data ) => {
                        const languageData = {};
                        const gplayDataIds = [];

                        data.forEach( ( page ) => {
                            if ( page.language ) {
                                const focusNewsData =
                                    page?.meta_box?.focusNews?.data?.map( ( item ) => {
                                        return {
                                            featured_media: item.featured_media,
                                            meta_box: item.meta_box,
                                            date: item.date,
                                        };
                                    } );
                                const aboutData = page?.meta_box?.about?.data?.map(
                                    ( item ) => {
                                        return {
                                            featured_media: item.featured_media,
                                            meta_box: item.meta_box,
                                        };
                                    },
                                );
                                const newsData = page?.meta_box?.news?.data?.map(
                                    ( item ) => {
                                        return {
                                            id: item.id,
                                            date: item.date,
                                            featured_media: item.featured_media,
                                            meta_box: {
                                                customDescription:
                                                    item.meta_box.customDescription,
                                                customTitle: item.meta_box.customTitle,
                                                article: item.meta_box.article,
                                                customImage: item.meta_box.customImage,
                                            },
                                        };
                                    },
                                );

                                page?.meta_box?.gplay?.data?.forEach( ( item ) => {
                                    gplayDataIds.push( item.id );
                                } );

                                languageData[ page.language ] = {
                                    featured_media: page.featured_media,
                                    seo: page.seo,
                                    excerpt: page.excerpt,
                                    meta_box: {
                                        ...page.meta_box,
                                        focusNews: {
                                            ...page.meta_box.focusNews,
                                            data: focusNewsData,
                                        },
                                        about: {
                                            ...page.meta_box.about,
                                            data: aboutData,
                                        },
                                        news: {
                                            ...page.meta_box.news,
                                            data: newsData,
                                        },
                                        gplay: {
                                            ...page.meta_box.gplay,
                                        },
                                    },
                                };
                            }
                        } );

                        // const { data: gplayData } = await fetchShorts( gplayDataIds );
                        // console.log( gplayData );

                        // // 將取得的 gplayData 依 id 建立索引，方便快速對應
                        // const fetchedShortById = new Map();
                        // if ( gplayData && Array.isArray( gplayData.value ) ) {
                        //     gplayData.value.forEach( ( item ) => {
                        //         if ( item && typeof item.id !== "undefined" ) {
                        //             fetchedShortById.set( item.id, item );
                        //         }
                        //     } );
                        // }

                        // // 依各語系，替換對應的 gplay 資料，並保留原本的 featured_media
                        // Object.keys( languageData ).forEach( ( lang ) => {
                        //     const gplayList = languageData?.[ lang ]?.meta_box?.gplay?.data;
                        //     if ( Array.isArray( gplayList ) ) {
                        //         languageData[ lang ].meta_box.gplay.data = gplayList.map( ( originalItem ) => {
                        //             const updated = fetchedShortById.get( originalItem?.id );
                        //             if ( updated ) {
                        //                 const preservedFeaturedMedia = originalItem?.featured_media;
                        //                 return { ...updated, featured_media: preservedFeaturedMedia };
                        //             }
                        //             return originalItem;
                        //         } );
                        //     }
                        // } );

                        return languageData;
                    },
                },
            );

            const currentData = computed( () => data.value[ locale.value ] || null );
            return { data: currentData, error };
        } catch ( err ) {
            return { data: ref( null ), error: ref( err ) };
        } finally {
            isPending.value = false;
        }
    };
    const fetchShortPage = async () => {

        isPending.value = true;

        try {
            const { data, error } = await useFetch(
                () => `${ API_URL }/pages?slug=short`,
                {
                    key: `fetchPage-short`,
                    transform: async ( data ) => {
                        const languageData = {};

                        data.forEach( ( page ) => {
                            if ( page.language ) {
                                const sections = page.meta_box.section.map(
                                    ( section ) => {
                                        const ids = section.shorts.map(
                                            ( short ) => short.id,
                                        );
                                        return { name: section.name, shorts: ids };
                                    },
                                );
                                languageData[ page.language ] = {
                                    featured_media: page.featured_media,
                                    seo: page.seo,
                                    excerpt: page.excerpt,
                                    meta_box: {
                                        ...page.meta_box,
                                        section: sections,
                                    },
                                };
                            }
                        } );

                        return languageData;
                    },
                },
            );

            const currentData = computed( () => data.value[ locale.value ] || null );
            return { data: currentData, error };
        } catch ( err ) {
            return { data: ref( null ), error: ref( err ) };
        } finally {
            isPending.value = false;
        }
    };
    const fetchAllPosts = async ( type = "news" ) => {
        isPending.value = true;

        try {
            const { data, error } = await useFetch(
                () => `${ API_URL }/${ type }?lang=${ locale.value }&per_page=100`,
                {
                    key: `fetchAllPosts-${ type }-${ locale.value }`, // Make cache key reactive to locale
                },
            );
            const processedData = await nuxtApp.runWithContext( () => Promise.all(
                data.value.map( ( post ) => _processPostMedia( post ) ),
            ) );
            return { data: ref( processedData ), error };
        } catch ( err ) {
            // console.error(`Error in fetchAllPosts:`, err);
            return {
                data: ref( [] ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };
    const fetchPosts = async ( type, ids ) => {
        if ( !ids || ids.length === 0 ) {
            return {
                data: ref( [] ),
                error: ref( null ),
            };
        }

        isPending.value = true;

        try {
            // Use fetchPost for each ID
            const promises = ids.map( ( id ) => fetchPost( type, id ) );
            const results = await Promise.all( promises );

            // Collect successful posts and errors
            const posts = [];
            const errors = [];

            for ( const result of results ) {
                if ( result.error.value ) {
                    errors.push( result.error.value );
                } else if ( result.data.value ) {
                    posts.push( result.data.value );
                }
            }

            // If all posts failed, return the first error
            if ( posts.length === 0 && errors.length > 0 ) {
                return {
                    data: ref( [] ),
                    error: ref( errors[ 0 ] ),
                };
            }

            // Return posts (may be partial if some succeeded)
            return {
                data: ref( posts ),
                error: errors.length > 0 ? ref( errors ) : ref( null ),
            };
        } catch ( err ) {
            // console.error(`Error in fetchPosts:`, err);
            return {
                data: ref( [] ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };
    const fetchPost = async ( type, id ) => {
        if ( !id ) {
            // console.error("Post ID is required");
            return {
                data: ref( null ),
                error: ref( new Error( "Post ID is required" ) ),
            };
        }

        isPending.value = true;

        try {
            // Clean up the ID if it's a string or number
            const cleanId = id.toString().trim();

            // Fetch post data
            const { data, error } = await useFetch(
                () => `${ API_URL }/${ type }/${ cleanId }`,
                {
                    key: `fetchPost-${ type }-${ cleanId }`,
                },
            );

            if ( error.value ) {
                // console.warn(
                //     `Error fetching ${type} with ID ${cleanId}:`,
                //     error.value,
                // );
                return { data, error };
            }

            // If no data was returned but also no error
            if ( !data.value ) {
                // console.warn(`No data returned for ${type} with ID ${cleanId}`);
                return {
                    data: ref( null ),
                    error: ref(
                        new Error(
                            `No data found for ${ type } with ID ${ cleanId }`,
                        ),
                    ),
                };
            }
            const postData = await _processPostMedia( data.value );
            let processedPostData = await _processPostCategory( type, postData );
            processedPostData = await _processPostTaxonomy(
                type,
                processedPostData,
            );

            // Add language alternatives for easy access
            if ( processedPostData.slug ) {
                processedPostData.languageAlternatives =
                    await _getLanguageAlternatives(
                        type,
                        processedPostData.slug,
                        processedPostData.id,
                    );
            }

            return { data: ref( processedPostData ), error };
        } catch ( err ) {
            // console.error(`Error in fetchPost (${type}/${id}):`, err);
            return {
                data: ref( null ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };
    // Helper function to get language alternatives
    const _getLanguageAlternatives = async ( type, slug, currentId ) => {
        try {
            const { data, error } = await nuxtApp.runWithContext( () => useFetch(
                () => `${ API_URL }/${ type }?slug=${ slug }`,
                {
                    key: `languageAlts-${ type }-${ slug }`,
                    transform: ( data ) => {
                        const alternatives = {};
                        data.forEach( ( post ) => {
                            if ( post.language ) {
                                alternatives[ post.language ] = {
                                    id: post.id,
                                    url: `${ post.language === "zh" ? "" : "/" + post.language }/${ type === "news" ? "news" : "awards" }/${ post.id }`,
                                };
                            }
                        } );
                        return alternatives;
                    },
                },
            ) );

            return error.value ? {} : data.value;
        } catch ( err ) {
            console.warn( "Failed to fetch language alternatives:", err );
            return {};
        }
    };
    const _processPostMedia = async ( data ) => {
        // Create a deep copy of the data to avoid mutation issues
        const postData = JSON.parse( JSON.stringify( data ) );

        //  Handle featuredMedia
        if ( postData.featured_media ) {
            const { data: featuredMediaData } = await nuxtApp.runWithContext( () => fetchMedia(
                postData.featured_media,
            ) );
            if ( featuredMediaData.value ) {
                postData.featured_media = featuredMediaData.value;
            }
        }
        // Handle customImage if it exists
        if ( postData.meta_box && postData.meta_box.customImage ) {
            // Prepare all image fetches
            const imagePromises = [];
            const imageKeys = [];

            // Check for tabletImage
            if (
                postData.meta_box.customImage.tabletImage &&
                Array.isArray( postData.meta_box.customImage.tabletImage ) &&
                postData.meta_box.customImage.tabletImage.length > 0
            ) {
                const tabletImageId =
                    postData.meta_box.customImage.tabletImage[ 0 ];

                imagePromises.push( fetchMedia( tabletImageId ) );
                imageKeys.push( "tabletImage" );
            }

            // Check for mobileImage
            if (
                postData.meta_box.customImage.mobileImage &&
                Array.isArray( postData.meta_box.customImage.mobileImage ) &&
                postData.meta_box.customImage.mobileImage.length > 0
            ) {
                const mobileImageId =
                    postData.meta_box.customImage.mobileImage[ 0 ];
                // console.log("Fetching mobile image", mobileImageId);

                imagePromises.push( fetchMedia( mobileImageId ) );
                imageKeys.push( "mobileImage" );
            }

            // Fetch all images in parallel
            if ( imagePromises.length > 0 ) {
                try {
                    const imageResults = await nuxtApp.runWithContext( () => Promise.all( imagePromises ) );

                    // Assign each result to the right property
                    imageResults.forEach( ( result, index ) => {
                        const keyName = imageKeys[ index ];
                        if ( !result.error.value && result.data.value ) {
                            // console.log(
                            //     `Successfully fetched ${keyName} image`,
                            // );
                            postData.meta_box.customImage[ keyName ] =
                                result.data.value;
                        } else {
                            // console.warn(
                            //     `Failed to fetch ${keyName} image:`,
                            //     result.error.value,
                            // );
                            postData.meta_box.customImage[ `${ keyName }Error` ] =
                                true;
                        }
                    } );
                } catch ( imgErr ) {
                    // console.error("Error fetching images:", imgErr);
                    postData.meta_box.imagesFetchFailed = true;
                }
            }
        }
        return postData;
    };
    const _processPostCategory = async ( type, data ) => {
        const { data: categoryData, error: categoryError } =
            await nuxtApp.runWithContext( () => fetchPostCategory( type === "news" ? "news" : "award" ) );
        if ( categoryError?.value ) {
            // console.error("Error fetching post category:", categoryError.value);
        } else {
            // modify the data to add the category name
            if ( data ) {
                const map = new Map(
                    categoryData.value.map( ( item ) => [ item.id, item.name ] ),
                );
                if ( type === "news" ) {
                    data[ "news-category" ] = data[ "news-category" ].map(
                        ( id ) => map.get( id ) ?? `Unknown(${ id })`,
                    );
                } else {
                    data[ "award-category" ] = data[ "award-category" ].map(
                        ( id ) => map.get( id ) ?? `Unknown(${ id })`,
                    );
                }
            }
        }
        return data;
    };
    const _processPostTaxonomy = async ( type, data ) => {
        const { data: taxonomyData, error: taxonomyError } =
            await nuxtApp.runWithContext( () => fetchPostTaxonomy( type === "news" ? "news" : "award" ) );
        if ( taxonomyError?.value ) {
            // console.error("Error fetching post taxonomy:", taxonomyError.value);
        } else {
            // modify the data to add the taxonomy name
            if ( data ) {
                const map = new Map(
                    taxonomyData.value.map( ( item ) => [ item.id, item.name ] ),
                );
                if ( type === "news" ) {
                    data[ "news-taxonomy" ] = data[ "news-taxonomy" ].map(
                        ( id ) => map.get( id ) ?? `Unknown(${ id })`,
                    );
                } else {
                    data[ "award-taxonomy" ] = data[ "award-taxonomy" ].map(
                        ( id ) => map.get( id ) ?? `Unknown(${ id })`,
                    );
                }
            }
        }
        return data;
    };
    const fetchSearchPosts = async ( keyword, type = "news" ) => {
        isPending.value = true;
        try {
            const { data, error } = await useFetch(
                () => `${ API_URL }/search?search=${ keyword }&subtype=${ type }`,
            );

            // Handle error or no data from the initial search
            if ( error.value ) {
                // console.warn(
                //     `Initial search failed for keyword "${keyword}", type "${type}":`,
                //     error.value,
                // );
                return { data: [], error: [ error.value ] }; // Match original return structure somewhat
            }
            if ( !data.value || data.value.length === 0 ) {
                return { data: [], error: [] }; // No results, no errors
            }

            // Start of the efficient fetching logic based on grouping by subtype
            const postsToFetchBySubtype = {};

            data.value.forEach( ( searchResultItem ) => {
                const postSubtype = searchResultItem.subtype; // Actual subtype from the search result item
                const postID = searchResultItem.id;

                if ( postSubtype && postID ) {
                    if ( !postsToFetchBySubtype[ postSubtype ] ) {
                        postsToFetchBySubtype[ postSubtype ] = [];
                    }
                    postsToFetchBySubtype[ postSubtype ].push( postID );
                }
            } );

            const fetchPromises = [];
            for ( const subtypeKey in postsToFetchBySubtype ) {
                if (
                    postsToFetchBySubtype.hasOwnProperty( subtypeKey ) &&
                    postsToFetchBySubtype[ subtypeKey ].length > 0
                ) {
                    // Call fetchPosts for each subtype group with its specific IDs
                    fetchPromises.push(
                        fetchPosts(
                            subtypeKey,
                            postsToFetchBySubtype[ subtypeKey ],
                        ),
                    );
                }
            }

            let combinedData = [];
            let combinedErrors = [];

            if ( fetchPromises.length > 0 ) {
                const fetchResults = await Promise.all( fetchPromises );
                fetchResults.forEach( ( res ) => {
                    // res.data is a ref, res.data.value is the array of posts
                    if ( res.data.value && res.data.value.length > 0 ) {
                        combinedData.push( ...res.data.value );
                    }
                    // res.error is a ref, res.error.value can be an error object, an array of errors, or null
                    if ( res.error.value ) {
                        if ( Array.isArray( res.error.value ) ) {
                            combinedErrors.push(
                                ...res.error.value.filter( ( e ) => e ),
                            ); // Add actual errors
                        } else {
                            combinedErrors.push( res.error.value );
                        }
                    }
                } );
            }
            // End of the efficient fetching logic

            return {
                data: combinedData, // An array of all fetched post objects
                error: combinedErrors, // An array of actual error objects/values encountered
            };
        } catch ( err ) {
            // Catch any unexpected errors during the process
            // console.error(
            //     `Critical error in fetchSearchPosts (keyword: ${keyword}, type: ${type}):`,
            //     err,
            // );
            // Ensure the return structure is consistent in case of a critical error
            const errorMessage =
                err instanceof Error ? err.message : String( err );
            return { data: [], error: [ errorMessage ] };
        } finally {
            isPending.value = false;
        }
    };
    const fetchRelatedPosts = async ( postId ) => {
        if ( !postId ) {
            return {
                data: ref( [] ),
                error: ref( new Error( "Post ID is required" ) ),
            };
        }
        isPending.value = true;
        const { data, error } = await useFetch(
            `/wordpress/wp-json/custom/v1/next-prev/${ postId }`,
        );
        return { data, error };
    };

    const fetchMedia = async ( id ) => {
        isPending.value = true;
        try {
            if ( import.meta.client ) {
                const data = await $fetch( `${ API_URL }/media/${ id }` );
                return { data: ref( data ), error: ref( null ) };
            } else {
                const { data, error } = await useFetch(
                    `${ API_URL }/media/${ id }`,
                );
                return { data, error };
            }
        } catch ( error ) {
            // console.error("Error in fetchMedia:", error);
            return { data: ref( null ), error: ref( error ) };
        } finally {
            isPending.value = false;
        }
    };

    const fetchMedias = async ( ids, key = null ) => {
        isPending.value = true;
        if ( !ids || ids.length === 0 ) {
            return {
                data: ref( [] ),
                error: ref( null ),
            };
        }

        try {
            const idStrings = ids.join( "," );
            const { data, error } = await useFetch(
                `${ API_URL }/media?include=${ idStrings }&per_page=100`,
                {
                    key: `fetchMedias-${ key }`,
                    transform: ( data ) => {
                        return data.map( ( item ) => {
                            return {
                                source_url: item.source_url,
                                alt_text: item.alt_text,
                                id: item.id,
                                media_details: {
                                    width: item.media_details.width,
                                    height: item.media_details.height,
                                },
                            };
                        } );
                    },
                },
            );

            return { data, error };
        } catch ( err ) {
            return {
                data: ref( [] ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };

    const fetchBrandsLotties = async () => {

        isPending.value = true;

        try {
            const { data, error } = await fetchBrands();

            if ( !data.value || data.value.length === 0 ) {
                return {
                    data: ref( {
                        pc: null,
                        tablet: null,
                        mobile: null,
                    } ),
                    error: ref( null ),
                };
            }

            const validBrands = data.value.filter(
                ( brand ) =>
                    brand?.language === locale.value &&
                    brand?.pcLottie?.[ 0 ] &&
                    brand?.tabletLottie?.[ 0 ] &&
                    brand?.mobileLottie?.[ 0 ],
            );

            if ( validBrands.length === 0 ) {
                return {
                    data: ref( {
                        pc: null,
                        tablet: null,
                        mobile: null,
                    } ),
                    error: ref( null ),
                };
            }

            const randomIndex = Math.floor( Math.random() * validBrands.length );
            const selectedBrand = validBrands[ randomIndex ];

            const pcLottieId = selectedBrand?.pcLottie?.[ 0 ] || null;
            const tabletLottieId = selectedBrand?.tabletLottie?.[ 0 ] || null;
            const mobileLottieId = selectedBrand?.mobileLottie?.[ 0 ] || null;

            const results = {
                pc: pcLottieId,
                tablet: tabletLottieId,
                mobile: mobileLottieId,
            };

            return { data: ref( results ), error: ref( null ) };
        } catch ( err ) {
            // console.error("Error in fetchBrandsLotties:", err);
            return {
                data: ref( {
                    pc: null,
                    tablet: null,
                    mobile: null,
                } ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };

    const fetchBrands = async ( ids = [] ) => {

        isPending.value = true;

        try {
            const { data, error } = await useFetch( `${ API_URL }/brand?per_page=100`, {
                key: "fetchBrands",
                transform: ( data ) => {
                    if ( ids.length > 0 ) {
                        const contents = ids
                            .map( ( id ) => data.find( ( item ) => item.id == id ) )
                            .filter( ( item ) => item !== undefined );
                        return contents.map( ( item ) => {
                            return { ...item.meta_box.brand, language: item.language };
                        } );
                    } else {
                        return data.map( ( item ) => {
                            return { ...item.meta_box.brand, language: item.language };
                        } );
                    }
                },
            } );

            return { data, error };
        } catch ( err ) {
            return {
                data: ref( [] ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };

    const fetchShorts = async ( ids = [], key = null ) => {

        isPending.value = true;

        try {
            const [
                { data: categoryList },
                { data: shortData, error },
            ] = await nuxtApp.runWithContext( () => Promise.all( [
                fetchPostCategory( "short" ),
                useFetch( `${ API_URL }/short?lang=${ locale.value }&per_page=100`, {
                    key: `fetchShort${ key ? `-${ key }-${ locale.value }` : `-${ locale.value }` }`,
                    transform: ( data ) => {
                        if ( ids.length > 0 ) {
                            return ids
                                .map( ( id ) => data.find( ( item ) => item.id == id ) )
                                .filter( ( item ) => item !== undefined )
                                .map( ( item ) => ( {
                                    id: item.id,
                                    date: item.date,
                                    image: item.featured_media,
                                    categories: item[ "short-category" ],
                                    tags: item[ "short-taxonomy" ],
                                    ...item.meta_box,
                                } ) );
                        } else {
                            return data.map( ( item ) => ( {
                                id: item.id,
                                featured_media: item.featured_media,
                                date: item.date,
                                categories: item[ "short-category" ],
                                tags: item[ "short-taxonomy" ],
                                language: item.language,
                                slug: item.slug,
                                ...item.meta_box,
                            } ) );
                        }
                    },
                } ),
            ] ) );

            // Ensure tag names for all tags used in current shorts via include (scheme A)
            const allTagIds = Array.from( new Set( ( shortData.value || [] )
                .flatMap( ( item ) => Array.isArray( item.tags ) ? item.tags : [] )
                .filter( ( id ) => id != null ) ) );

            if ( allTagIds.length > 0 ) {
                // Batch by 100 and fetch terms from custom taxonomy `short-taxonomy`
                const batches = _chunkArray( allTagIds, 100 );

                await Promise.all( batches.map( async ( batchIds ) => {

                    const includeParam = batchIds.join( "," );
                    const { data: termsData } = await nuxtApp.runWithContext( () => useFetch( `${ API_URL }/short-taxonomy?per_page=100&hide_empty=false&_fields=id,name,slug&include=${ includeParam }`,
                        {
                            key: `ensureTerms-short-taxonomy-${ includeParam }`,
                            transform: ( data ) =>
                                data.map( ( item ) => ( {
                                    id: item.id,
                                    name: item.name,
                                    slug: item.slug,
                                    language: item.language,
                                } ) ),
                        },
                    ) );

                    const terms = termsData.value || [];
                    terms.forEach( ( term ) => {
                        _termDictionary.set( _getTermDictKey( "short-taxonomy", term.id ), term.name );
                    } );

                } ) );
            }

            const processedContent = shortData.value.map( ( item ) => {
                const categories = item.categories.map(
                    ( catId ) =>
                        categoryList.value.find( ( category ) => category.id === catId,
                        )?.name,
                );
                const tags = ( item.tags || [] )
                    .map( ( tagId ) => _termDictionary.get( _getTermDictKey( "short-taxonomy", tagId ) ) )
                    .filter( ( name ) => !!name );

                return {
                    ...item,
                    categories: categories,
                    tags: tags,
                };
            } );

            return {
                data: ref( processedContent ),
                error,
            };
        } catch ( err ) {
            // console.error( "Error in fetchShorts:", err );
            return {
                data: ref( [] ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };

    // 處理搜索結果內容，提取關鍵詞前後各50個字符
    const extractContentAroundKeyword = ( content, keyword ) => {
        if ( !content || !keyword || typeof content !== "string" ) {
            return content;
        }

        // 對關鍵詞進行正則表達式安全轉義
        const escapeRegExp = ( string ) => {
            return string.replace( /[.*+?^${}()|[\]\\]/g, "\\$&" );
        };

        const safeKeyword = escapeRegExp( keyword );
        const regex = new RegExp( safeKeyword, "i" ); // 不區分大小寫
        const match = content.match( regex );

        if ( !match ) {
            // 如果找不到關鍵詞，返回內容的前100個字符
            return content.length > 100
                ? content.substring( 0, 100 ) + "..."
                : content;
        }

        const matchIndex = match.index;
        const matchLength = match[ 0 ].length;

        // 計算前後各50個字符的位置
        let startIndex = Math.max( 0, matchIndex - 50 );
        let endIndex = Math.min( content.length, matchIndex + matchLength + 50 );

        // 創建截取的內容
        let result = "";

        // 開頭被截斷時添加...
        if ( startIndex > 0 ) {
            result += "...";
        }

        // 添加關鍵詞前後的內容
        result += content.substring( startIndex, endIndex );

        // 結尾被截斷時添加...
        if ( endIndex < content.length ) {
            result += "...";
        }

        return result;
    };

    const fetchSearchResult = async ( keyword ) => {
        isPending.value = true;

        const pageMapping = [
            { slug: "home", path: "/", zh: "首頁", en: "Home", ja: "ホーム", ko: "홈" },
            { slug: "about", path: "/about", zh: "關於橘子", en: "About gamania", ja: "gamaniaについて", ko: "기업 소개" },
            { slug: "location", path: "/location", zh: "總部位置", en: "Headquarters Location", ja: "グループ本部アクセス", ko: "그룹 본사 위치" },
        ];

        try {
            const { data, error } = await useFetch(
                `/wordpress/wp-json/custom/v1/search?s=${ keyword }&lang=${ locale.value }&post_type=page,short,news,award&per_page=100`,
            );

            // 處理搜索結果數據
            if ( data.value && Array.isArray( data.value.results ) ) {
                const processPromises = data.value.results.map( async ( item ) => {
                    // 根據不同類型處理數據
                    if ( item.type === "page" ) {
                        // 處理頁面類型
                        // 檢查是否有對應的 slug 映射
                        const slug = pageMapping.find( map => map[ locale.value ] === item.title )?.slug;

                        if ( !slug ) {
                            return null; // 若沒有 mapping 到 slug，則不回傳
                        }

                        const { data: pageData } = await fetchPage( slug );
                        const { data: imageData } = await fetchMedia(
                            pageData.value?.featured_media,
                        );

                        const excerpt =
                            pageData.value?.excerpt?.rendered?.replace(
                                /<[^>]*>/g,
                                "",
                            ) || "";
                        const processedExcerpt = extractContentAroundKeyword(
                            excerpt,
                            keyword,
                        );

                        return {
                            thumbnail: imageData.value?.source_url,
                            title: item.title,
                            content: [ processedExcerpt ],
                            date: item.date,
                            url: pageMapping.find( map => map[ locale.value ] === item.title )?.path,
                        };
                    } else if ( item.type === "short" ) {
                        // 處理短片類型
                        const { data: shortData } = await fetchShorts(
                            [ item.id ],
                            `search-short-${ item.id }`,
                        );

                        const shortDescription =
                            shortData.value?.[ 0 ]?.description || "";
                        const processedDescription =
                            extractContentAroundKeyword(
                                shortDescription,
                                keyword,
                            );

                        const { data: shortImage } = await fetchMedias(
                            [ shortData.value?.[ 0 ]?.image ],
                            `search-short-image-${ item.id }`,
                        );

                        return {
                            thumbnail: shortImage.value?.[ 0 ]?.source_url,
                            title: shortData.value?.[ 0 ]?.videoTitle,
                            content: processedDescription,
                            date: item.date,
                            url: `/gplay/${ item.id }`,
                        };
                    } else if ( item.type === "news" ) {
                        // 處理新聞類型
                        const { data: newsData } = await fetchPost(
                            "news",
                            item.id,
                        );

                        // 獲取分類
                        let category = newsData.value?.[ "news-category" ]?.[ 0 ];

                        const newsContent =
                            newsData.value?.meta_box?.htmlContent?.replace(
                                /<[^>]*>/g,
                                "",
                            ) || "";
                        const processedNewsContent =
                            extractContentAroundKeyword( newsContent, keyword );

                        return {
                            thumbnail:
                                newsData.value?.featured_media?.source_url,
                            title: newsData.value?.meta_box?.customTitle?.join( " " ),
                            content: processedNewsContent,
                            date: item.date,
                            url: `/news/${ item.id }`,
                            category: category,
                        };
                    } else if ( item.type === "award" ) {
                        // 處理獎項類型
                        const { data: awardData } = await fetchPost(
                            "award",
                            item.id,
                        );

                        // 獲取分類
                        let category = awardData.value?.[ "award-category" ]?.[ 0 ];

                        const awardContent =
                            awardData.value?.meta_box?.htmlContent?.replace(
                                /<[^>]*>/g,
                                "",
                            ) || "";
                        const processedAwardContent =
                            extractContentAroundKeyword( awardContent, keyword );

                        return {
                            thumbnail:
                                awardData?.value?.featured_media?.source_url,
                            title: awardData.value?.meta_box?.customTitle?.join( " " ),
                            content: processedAwardContent,
                            date: item.date,
                            url: `/awards/${ item.id }`,
                            category: category,
                        };
                    }

                    return item;
                } );

                // 等待所有處理完成
                const processedResults = await Promise.all( processPromises );
                // 更新結果
                data.value.processedResult = processedResults;
            }

            return { data, error };
        } catch ( err ) {
            // console.error("Error in fetchSearchResult:", err);
            return {
                data: ref( [] ),
                error: ref( err ),
            };
        } finally {
            isPending.value = false;
        }
    };

    return {
        isPending,
        fetchPostCategory,
        fetchPage,
        fetchHomePage,
        fetchShortPage,
        fetchAllPosts,
        fetchPosts,
        fetchPost,
        fetchSearchPosts,
        fetchBrandsLotties,
        fetchBrands,
        fetchRelatedPosts,
        fetchShorts,
        fetchMedia,
        fetchMedias,
        fetchSearchResult
    };
};
